import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { BusinessEntity, NextCheckNumber } from "@/api/types";
import "./CheckNumberSetting.css";

/**
 * ef-062-8 - Setup's "Check numbering" section (rendered by `Access.tsx`, which passes it nothing). The owner picks an entity,
 * sees the number its next printed check will carry and the last check on record, and sets a new starting number so printed
 * checks continue from the stock the farm ordered. Payroll and register checks share ONE sequence per entity.
 *
 * It owns its data: the active entities, and `GET|PUT /api/demo/entities/{id}/next-check-number`. The server is the only owner
 * of the rules (1..999,999,999, never at or below a number already on record); this component only refuses what it cannot send
 * (an empty or non-integer field) and shows the server's own sentence otherwise.
 *
 * @ui-check: once a check is on record the number can only go up - a save at or below "Last check on record" is refused by the
 * server, shown in the one role=alert, and nothing changes.
 */
type SectionState = "loading" | "error" | "empty" | "ready";

const MAX_DIGITS_HINT = "Enter a whole number between 1 and 999,999,999.";

export function CheckNumberSetting() {
  const entityFieldId = useId();
  const numberFieldId = useId();
  const messageId = useId();
  const headingId = useId();

  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [entityId, setEntityId] = useState<string>("");

  const [current, setCurrent] = useState<NextCheckNumber | null>(null);
  const [numberLoading, setNumberLoading] = useState(false);
  const [numberError, setNumberError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  // The entity list, once. Active entities only: an archived one is not offered anywhere else in Setup either.
  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .getAllEntitiesIncludingArchived(controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        const active = all.filter((e) => e.archivedAt === null);
        setEntities(active);
        setEntityId((picked) => picked || (active.find((e) => e.isDefault) ?? active[0])?.id || "");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setListError(problemMessage(err, "Could not load the entities."));
      });
    return () => controller.abort();
  }, []);

  // The selected entity's number. Keyed by the entity; a stale answer for an entity the owner has already left is dropped.
  useEffect(() => {
    if (!entityId) return;
    const controller = new AbortController();
    setNumberLoading(true);
    setNumberError(null);
    demoApi
      .getNextCheckNumber(entityId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCurrent(result);
        setDraft(String(result.nextCheckNumber));
        setNumberLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setNumberError(problemMessage(err, "Could not load this entity's check number."));
        setNumberLoading(false);
      });
    return () => controller.abort();
  }, [entityId]);

  function handleEntityChange(id: string): void {
    if (id === entityId) return;
    setEntityId(id);
    setCurrent(null);
    setDraft("");
    setSaved(false);
    setRefusal(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!entityId || saving || numberLoading) return;

    setSaved(false);
    setRefusal(null);
    const trimmed = draft.trim();
    if (!/^-?\d+$/.test(trimmed)) {
      setRefusal(MAX_DIGITS_HINT);
      return;
    }

    setSaving(true);
    try {
      const updated = await demoApi.setNextCheckNumber(entityId, Number(trimmed));
      setCurrent(updated);
      setDraft(String(updated.nextCheckNumber));
      setSaved(true);
    } catch (err) {
      setRefusal(
        problemFieldErrors(err)?.nextCheckNumber?.[0] ?? problemMessage(err, "Could not save this check number."),
      );
    } finally {
      setSaving(false);
    }
  }

  const state: SectionState =
    listError !== null || (numberError !== null && !numberLoading)
      ? "error"
      : entities === null || numberLoading || (entityId !== "" && current === null && numberError === null)
        ? "loading"
        : entities.length === 0
          ? "empty"
          : "ready";

  return (
    <section
      className="access__section checknumber"
      aria-labelledby={headingId}
      data-state={state}
    >
      <h2 className="access__section-title" id={headingId}>
        Check numbering
      </h2>
      <p className="access__lede">
        The number the next printed check carries, for each entity. Payroll and register checks share one
        sequence. Set it to continue from the check stock you ordered or the last check you printed; once a check
        is printed the number can only go up, so a number is never used twice.
      </p>

      {listError !== null && (
        <p className="checknumber__error" role="alert">
          {listError}
        </p>
      )}

      {listError === null && entities !== null && entities.length === 0 && (
        <p className="access__note">No active entities.</p>
      )}

      {listError === null && entities !== null && entities.length > 0 && (
        <form className="checknumber__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="checknumber__field">
            <label htmlFor={entityFieldId}>Entity</label>
            <select id={entityFieldId} value={entityId} onChange={(e) => handleEntityChange(e.target.value)}>
              {entities.map((entity) => (
                <option key={entity.id} value={entity.id} label={entity.name} />
              ))}
            </select>
          </div>

          <div className="checknumber__field">
            <label htmlFor={numberFieldId}>Next check number</label>
            <input
              id={numberFieldId}
              type="number"
              inputMode="numeric"
              min={1}
              max={999999999}
              step={1}
              value={draft}
              disabled={numberLoading || current === null}
              onChange={(e) => {
                setDraft(e.target.value);
                setSaved(false);
                setRefusal(null);
              }}
              aria-invalid={refusal !== null ? true : undefined}
              aria-describedby={refusal !== null ? messageId : undefined}
            />
            {current !== null && (
              <p className="checknumber__record">
                {current.highestOnRecord === null
                  ? "No checks printed yet."
                  : `Last check on record: #${current.highestOnRecord}`}
              </p>
            )}
          </div>

          {numberError !== null && !numberLoading && (
            <p className="checknumber__error" role="alert">
              {numberError}
            </p>
          )}
          {refusal !== null && (
            <p className="checknumber__error" id={messageId} role="alert">
              {refusal}
            </p>
          )}

          <div className="checknumber__actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={saving || numberLoading || current === null}
            >
              {saving ? "Saving…" : "Save check number"}
            </button>
            {saved && !saving && (
              <span className="checknumber__saved" role="status">
                Saved.
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
